import { Article } from '@generated'
import { getKontentImage } from '@lib/kontent'
import { useState, useEffect } from 'react'
import ProductSlider from '../Sliders/ProductSlider'
import dynamic from 'next/dynamic'
import { preloadImage } from '@lib/preload-image'
import { imgix } from '@lib/imigx'
import { cn } from '@lib/cn'
import ProductDetailsPanel from './ProductDetails/ProductDetailsPanel'
import { RolexContainer } from '@components/_rolex/Container'
import ProductGallery from '../ProductGallery'
import Indicators from '@components/_rolex/sliders/Indicators'

const Product360View = dynamic(() => import('./ProductImage/Product360View'))

type Props = {
  data: Article
}

const ProductInfo = ({ data }: Props) => {
  const [index, setIndex] = useState(0)
  const [selectedDesktopImage, setSelectedDesktopImage] = useState(data.images[0])
  const [is360ViewOpen, setIs360ViewOpen] = useState(false)

  const { modelName } = data

  const imagesSlider = data.images.filter((url) => !url.includes('_360'))
  const images360 = data.images
    .filter((url) => url.includes('_360'))
    .sort((a, b) => {
      // Updated regex to handle both url formats: _360_4.png and _360_(4).png
      const getIndex = (url: string) => {
        const match = url.match(/_360_\(?(\d+)\)?\.png$/)
        return parseInt(match?.[1] || '0', 10)
      }
      return getIndex(b) - getIndex(a)
    })
    .map((url) => imgix(url, { width: 850 }))
  const has360Images = images360.length > 0

  // Preload the first 360 image
  useEffect(() => {
    if (has360Images) {
      // Returns a promise, but we don't need to wait for it to resolve
      preloadImage(images360[0])
    }
  }, [has360Images, images360])

  useEffect(() => {
    setSelectedDesktopImage(data.images[0])
  }, [data.images])

  return (
    <RolexContainer
      className={cn('mx-0 -mt-[60px] text-rolexShade-900', 'md:mx-auto md:mt-0 md:text-white')}
    >
      <div
        className={cn(
          'flex w-[100vw] flex-col',
          'md:grid md:w-full md:grid-cols-12 md:flex-col md:gap-1',
        )}
      >
        <div className="hidden md:col-span-6 md:block">
          <ProductDetailsPanel data={data} />
        </div>
        <div className="block w-full items-center justify-center bg-black md:col-span-6">
          <ProductSlider
            activeIndex={index}
            onChange={setIndex}
            desktopSource={getKontentImage(selectedDesktopImage, { width: 750, height: 750 })}
            mobileSources={imagesSlider.map((url) =>
              getKontentImage(url, { width: 750, height: 750 }),
            )}
            title={`Rolex ${modelName}`}
            onSet360View={has360Images ? () => setIs360ViewOpen(true) : undefined}
          />
        </div>
        <div className="block md:hidden">
          <Indicators
            variant="animated"
            className="my-0 bg-black py-5"
            activeIndex={index}
            count={imagesSlider.length}
            onChangeIndex={setIndex}
          />
        </div>
        <div className="md:hidden">
          <ProductDetailsPanel data={data} />
        </div>
      </div>
      {has360Images && (
        <Product360View
          images={images360}
          imagesSlider={imagesSlider}
          title={`Rolex ${modelName} Modal`}
          isOpen={is360ViewOpen}
          onClose={() => setIs360ViewOpen(false)}
        />
      )}
      <ProductGallery data={data} onSelectImage={(url) => setSelectedDesktopImage(url)} />
    </RolexContainer>
  )
}

export default ProductInfo
